import { useEffect } from "react";

const SITE = "Soul Radar";
const BASE = process.env.REACT_APP_BACKEND_URL;

function setTag(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) { el = document.createElement(attrs.tag || "meta"); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => { if (k !== "tag" && v != null) el.setAttribute(k, v); });
  return el;
}

/* Sets title, description, canonical, Open Graph and optional JSON-LD for the current page. */
export function useMeta({ title, description, path, image, type = "website", jsonLd, noindex = false }) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE}` : `${SITE} – Your Soul Event Finder`;
    const url = `${BASE}${path || window.location.pathname}`;
    const img = image || `${BASE}/brand/og-image.png`;
    document.title = fullTitle;
    setTag('meta[name="description"]', { name: "description", content: description || "" });
    setTag('link[rel="canonical"]', { tag: "link", rel: "canonical", href: url });
    setTag('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow" });
    [["og:title", fullTitle], ["og:description", description || ""], ["og:url", url], ["og:image", img], ["og:type", type], ["og:site_name", SITE]]
      .forEach(([p, c]) => setTag(`meta[property="${p}"]`, { property: p, content: c }));
    [["twitter:card", "summary_large_image"], ["twitter:title", fullTitle], ["twitter:description", description || ""], ["twitter:image", img]]
      .forEach(([n, c]) => setTag(`meta[name="${n}"]`, { name: n, content: c }));
    let ld = document.getElementById("ld-json");
    if (jsonLd) {
      if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "ld-json"; document.head.appendChild(ld); }
      ld.textContent = JSON.stringify(jsonLd);
    } else if (ld) ld.remove();
  }, [title, description, path, image, type, noindex, JSON.stringify(jsonLd)]); // eslint-disable-line
}

export function eventJsonLd(e, lang) {
  const status = { cancelled: "EventCancelled", postponed: e.new_date_tba ? "EventPostponed" : "EventRescheduled", scheduled: "EventScheduled", sold_out: "EventScheduled" }[e.public_status || "scheduled"] || "EventScheduled";
  const ld = {
    "@context": "https://schema.org", "@type": "Event", name: e.name, url: `${BASE}/events/${e.slug}`,
    eventStatus: `https://schema.org/${status}`, eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: { "@type": "Place", name: e.venue, address: { "@type": "PostalAddress", streetAddress: e.address || undefined, postalCode: e.postcode || undefined, addressLocality: e.city, addressCountry: e.country } },
  };
  if (!e.new_date_tba && e.start_date) ld.startDate = e.start_time ? `${e.start_date}T${e.start_time}:00` : e.start_date;
  if (!e.new_date_tba && e.end_date) ld.endDate = e.end_time ? `${e.end_date}T${e.end_time}:00` : e.end_date;
  if (e.short_description || e.description) ld.description = e.short_description || e.description.slice(0, 300);
  if (e.main_image) ld.image = [e.main_image];
  if (e.organizer_name) ld.organizer = { "@type": "Organization", name: e.organizer_name, url: `${BASE}/users/${e.organizer_id}` };
  if (e.djs?.length) ld.performer = e.djs.map(n => ({ "@type": "Person", name: n }));
  if (e.free_event) ld.isAccessibleForFree = true;
  const price = e.price ?? e.nighter_price ?? e.pass_price;
  if (e.ticket_url || price != null || e.free_event) ld.offers = { "@type": "Offer", url: e.ticket_url || `${BASE}/events/${e.slug}`, price: e.free_event ? 0 : price ?? undefined, priceCurrency: e.currency || "EUR", availability: e.public_status === "sold_out" ? "https://schema.org/SoldOut" : "https://schema.org/InStock" };
  return ld;
}
